import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coffee, Instagram } from "lucide-react";
import { BUY_ME_A_COFFEE_URL } from "../lib/brand";
import { useTheme } from "../context/ThemeContext";
import { InstallButton } from "./InstallButton";
import { api } from "../lib/api";

export const Footer = () => {
  const { theme } = useTheme();
  const [links, setLinks] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/footer-links")
      .then((r) => { if (alive) setLinks(r.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const title = theme.footer_title ?? "";
  const description = theme.footer_description ?? "";
  const kicker = theme.footer_kicker ?? "";
  const tagline = theme.footer_tagline ?? "";
  const taglineUrl = (theme.footer_tagline_url ?? "").trim();
  const footerBg = (theme.footer_bg_color || "").trim();

  return (
    <footer
      className="border-t border-black/10"
      style={{ backgroundColor: footerBg || "transparent" }}
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row">
          <div>
            {title ? (
              <p
                className="font-display text-xl font-black tracking-tight text-[var(--brand-ink)]"
                data-testid="footer-title"
              >
                {title}
              </p>
            ) : null}
            {description ? (
              <p
                className="mt-2 max-w-md text-sm text-[var(--brand-body)]"
                data-testid="footer-description"
              >
                {description}
              </p>
            ) : null}
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
              {links.map((link, idx) => {
                const isInternal = link.to.startsWith("/");
                const LinkTag = isInternal ? Link : "a";
                const props = isInternal ? { to: link.to } : { href: link.to, target: link.target === "blank" ? "_blank" : undefined, rel: link.target === "blank" ? "noreferrer" : undefined };

                return (
                  <React.Fragment key={link.id}>
                    {idx > 0 ? <span className="text-[var(--brand-body)]">·</span> : null}
                    <LinkTag
                      {...props}
                      className="text-[var(--brand-ink)] underline decoration-[#6CABDD] underline-offset-4 hover:text-[#5A9AC9]"
                      data-testid={`footer-link-${link.id}`}
                    >
                      {link.label}
                    </LinkTag>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col gap-3 text-sm text-[var(--brand-body)] sm:items-end sm:text-right">
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <InstallButton
                className="self-start sm:self-end"
                testId="footer-install-button"
                disabled={theme.install_button_visible === false}
              />
              <a
                href="https://www.instagram.com/letsallsingtogetherdotcom/"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                aria-label="LAST on Instagram"
                className="inline-flex h-10 w-10 items-center justify-center self-start rounded-md border border-black/10 bg-white text-[var(--brand-ink)] transition-colors hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF] hover:text-white sm:self-end"
              >
                <Instagram size={16} strokeWidth={2.5} />
              </a>
              <a
                href={BUY_ME_A_COFFEE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-buy-coffee"
                className="inline-flex items-center gap-2 self-start rounded-md bg-[#FFDD00] px-4 py-2 font-display text-sm font-bold text-[var(--brand-ink)] transition-colors hover:bg-[#0A0F17] hover:text-[#FFDD00] sm:self-end"
              >
                <span aria-hidden="true">🍔</span> Buy LAST a Batch!
              </a>
            </div>
            {kicker ? (
              <span
                className="label-kicker text-[var(--brand-ink)]"
                data-testid="footer-kicker"
              >
                {kicker}
              </span>
            ) : null}
            {tagline ? (
              taglineUrl ? (
                <a
                  href={taglineUrl}
                  target={/^https?:\/\//i.test(taglineUrl) ? "_blank" : undefined}
                  rel={/^https?:\/\//i.test(taglineUrl) ? "noreferrer" : undefined}
                  data-testid="footer-tagline"
                  className="text-[var(--brand-body)] underline decoration-[#6CABDD] underline-offset-4 hover:text-[var(--brand-ink)]"
                >
                  {tagline}
                </a>
              ) : (
                <span data-testid="footer-tagline">{tagline}</span>
              )
            ) : null}
          </div>
        </div>
      </div>
    </footer>
  );
};
